/**
 * ReportJobButton — appears on every public job detail page (iter-135).
 * Opens a small non-blocking form. Anonymous; email is optional. Fires
 * POST /api/public/jobs/{job_id}/report and shows a thank-you toast.
 */
import React, { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REASONS = [
    { v: "scam_suspected", label: "Scam suspected" },
    { v: "misleading",     label: "Misleading" },
    { v: "discriminatory", label: "Discriminatory" },
    { v: "other",          label: "Other" },
];

export default function ReportJobButton({ jobId }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("scam_suspected");
    const [details, setDetails] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    if (!jobId) return null;

    const submit = async () => {
        if (details.trim().length < 5) {
            toast.error("Please add a few words of detail.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/public/jobs/${jobId}/report`, {
                reason,
                details: details.trim(),
                reporter_email: email.trim() || null,
            });
            toast.success("Thanks — our team will review within 24 hours.");
            setOpen(false);
            setDetails(""); setEmail("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed");
        } finally { setBusy(false); }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-rose-600 underline"
                data-testid="report-job-btn"
            >
                <Flag size={12} /> Report this job
            </button>
            {open && (
                <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4"
                     data-testid="report-job-modal" role="dialog" aria-modal="true">
                    <div className="w-full max-w-md rounded-2xl bg-background border border-border shadow-2xl p-5">
                        <h3 className="font-heading font-semibold text-lg">Report this job</h3>
                        <p className="text-xs text-muted-foreground">
                            We review every report. Anonymous submissions are welcome — email is optional.
                        </p>
                        <div className="mt-4 space-y-3">
                            <label className="block text-xs font-semibold">Reason</label>
                            <select
                                value={reason} onChange={(e) => setReason(e.target.value)}
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                                data-testid="report-reason"
                            >
                                {REASONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                            </select>
                            <label className="block text-xs font-semibold">Details</label>
                            <textarea
                                rows={4} value={details} maxLength={2000}
                                onChange={(e) => setDetails(e.target.value)}
                                placeholder="Tell us what's wrong…"
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                                data-testid="report-details"
                            />
                            <label className="block text-xs font-semibold">Your email (optional)</label>
                            <input
                                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                                data-testid="report-email"
                            />
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button" onClick={() => setOpen(false)} disabled={busy}
                                className="text-xs px-3 py-2 rounded-full border border-border hover:bg-muted"
                            >Cancel</button>
                            <button
                                type="button" onClick={submit} disabled={busy}
                                className="text-xs px-3 py-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white font-semibold inline-flex items-center gap-1"
                                data-testid="report-submit"
                            >
                                {busy ? <Loader2 size={12} className="animate-spin" /> : <Flag size={12} />}
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
